import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import Navbar from "@/components/Navbar";
import Hero from "@/components/Hero";
import FeatureMarquee from "@/components/FeatureMarquee";
import About from "@/components/About";
import Rooms from "@/components/Rooms";
import Cuisine from "@/components/Cuisine";
import Campfire from "@/components/Campfire";
import Testimonials from "@/components/Testimonials";
import Attractions from "@/components/Attractions";
import PhotoGallery from "@/components/PhotoGallery";
import { PHOTO_GALLERY } from "@/data/site";
import BookingForm from "@/components/BookingForm";
import Contact from "@/components/Contact";
import Footer from "@/components/Footer";
import FloatingButtons from "@/components/FloatingButtons";
import { SITE, FAQS } from "@/data/site";
import { Toaster } from "sonner";
import "@/App.css";

function App() {
  useEffect(() => {
    document.documentElement.style.scrollBehavior = "smooth";
  }, []);

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LodgingBusiness",
    name: SITE.brand,
    image: [
      "https://images.unsplash.com/photo-1660294534711-75a9474fecb0?auto=format&fit=crop&w=1600&q=80",
    ],
    "@id": SITE.domain,
    url: SITE.domain,
    telephone: `+${SITE.phoneE164}`,
    email: SITE.email,
    priceRange: "₹₹",
    address: {
      "@type": "PostalAddress",
      streetAddress: "Mudigere",
      addressLocality: "Chikkamagaluru",
      addressRegion: "Karnataka",
      postalCode: "577132",
      addressCountry: "IN",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: 13.1333,
      longitude: 75.6333,
    },
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: [
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday",
          "Saturday",
          "Sunday",
        ],
        opens: "00:00",
        closes: "23:59",
      },
    ],
    sameAs: [],
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: SITE.domain },
      {
        "@type": "ListItem",
        position: 2,
        name: "Stays",
        item: `${SITE.domain}/#rooms`,
      },
    ],
  };

  return (
    <div className="min-h-screen bg-[var(--pam-bg)] text-[var(--pam-text)] font-sans">
      <Helmet>
        <html lang="en" />
        <title>
          Peak Adventures Malnad | Authentic Homestays in Chikkamagaluru,
          Mudigere, Kalasa & Sakleshpur
        </title>
        <meta
          name="description"
          content="Authentic Malnad hospitality in the Western Ghats. Traditional cottages, coffee-estate stays, bonfire nights and home-cooked Malnad cuisine across Mudigere, Chikkamagaluru, Kalasa & Sakleshpur."
        />
        <link rel="canonical" href={SITE.domain} />
        <meta name="robots" content="index,follow,max-image-preview:large" />

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={SITE.brand} />
        <meta
          property="og:title"
          content="Peak Adventures Malnad — Authentic Homestays in the Western Ghats"
        />
        <meta
          property="og:description"
          content="Traditional Malnad cottages across Mudigere, Chikkamagaluru, Kalasa & Sakleshpur — coffee estates, waterfalls, bonfire nights."
        />
        <meta property="og:url" content={SITE.domain} />
        <meta
          property="og:image"
          content="https://images.unsplash.com/photo-1660294534711-75a9474fecb0?auto=format&fit=crop&w=1600&q=80"
        />

        <meta name="twitter:card" content="summary_large_image" />

        <script type="application/ld+json">
          {JSON.stringify(localBusinessSchema)}
        </script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

      <Navbar />
      <main>
        <Hero />
        <FeatureMarquee />
        <About />
        <Rooms />
        <Cuisine />
        <Campfire />
        <Testimonials />
        <Attractions />
        <PhotoGallery images={PHOTO_GALLERY} />
        <BookingForm />
        <Contact />
      </main>
      <Footer />
      <FloatingButtons />
      <Toaster position="top-center" richColors />
    </div>
  );
}

export default App;
